import { Tooltip } from '@neo/primitives/tooltip'
import { ReactComponent as Amex } from '@shared/assets/payment-brands/ic-amex.svg'
import { ReactComponent as Diners } from '@shared/assets/payment-brands/ic-dinersclub.svg'
import { ReactComponent as Discover } from '@shared/assets/payment-brands/ic-discover.svg'
import { ReactComponent as JCB } from '@shared/assets/payment-brands/ic-jcb.svg'
import { ReactComponent as Maestro } from '@shared/assets/payment-brands/ic-maestro.svg'
import { ReactComponent as Mastercard } from '@shared/assets/payment-brands/ic-mastercard.svg'
import { ReactComponent as RuPay } from '@shared/assets/payment-brands/ic-rupay.svg'
import { ReactComponent as UnionPay } from '@shared/assets/payment-brands/ic-unionpay.svg'
import { ReactComponent as Visa } from '@shared/assets/payment-brands/ic-visa.svg'
import { AlertOctagon, CheckCircle2, CreditCard, Loader2 } from 'lucide-react'
import { createMultiRuleValidation, type ValidationRule } from 'v2/shared/validation-schemas'
import { z } from 'zod'
import { resolvePaymentBrandKey } from './payment-brand'

export enum ValidationStatus {
  IDLE = 'idle',
  VALID = 'valid',
  INVALID = 'invalid',
  LOADING = 'loading',
}
export enum Currency {
  INR = 'INR',
  USD = 'USD',
}
export const getPostalCodeLabel = (country: string): string =>
  ({
    US: 'ZIP Code',
    CA: 'Postal Code',
    IN: 'PIN Code',
  })[country] ?? 'Postal Code'

// Export enum values as types for reuse
export type ValidationStatusType = `${ValidationStatus}`

interface PaymentBrandIconsProps {
  brand: string
  size?: number
}

export enum GstStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

// Switches on the normalised key rather than the raw string — see
// resolvePaymentBrandKey for why every spelling used to need its own case.
export const PaymentBrandIcons = ({ brand, size = 28 }: PaymentBrandIconsProps) => {
  switch (resolvePaymentBrandKey(brand)) {
    case 'visa':
      return <Visa width={size} height={size} />
    case 'mastercard':
      return <Mastercard width={size} height={size} />
    case 'amex':
      return <Amex width={size} height={size} />
    case 'discover':
      return <Discover width={size} height={size} />
    case 'diners':
      return <Diners width={size} height={size} />
    case 'jcb':
      return <JCB width={size} height={size} />
    case 'maestro':
      return <Maestro width={size} height={size} />
    case 'unionpay':
      return <UnionPay width={size} height={size} />
    case 'rupay':
      return <RuPay width={size} height={size} />
    default:
      return <CreditCard width={size} height={size} className="stroke-1" />
  }
}

export enum PaymentMethod {
  CARD = 'card',
  BANK_TRANSFER = 'bank_transfer',
  BANK_TRANSFER_ACH = 'bank_transfer_ach',
  COUPON = 'coupon',
}

export enum PaymentStatus {
  CREDITED = 'CREDITED',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  PARTIALLY_REFUNDED_DISPUTED = 'PARTIALLY REFUNDED & DISPUTED',
  PARTIALLY_REFUNDED = 'PARTIALLY REFUNDED',
  REFUNDED_DISPUTED = 'REFUNDED & DISPUTED',
  REFUNDED = 'REFUNDED',
  DISPUTED = 'DISPUTED',
}

export const GST_REGEX =
  /^([0]{1}[1-9]{1}|[1-2]{1}[0-9]{1}|[3]{1}[0-7]{1})([a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}[1-9a-zA-Z]{1}[zZ]{1}[0-9a-zA-Z]{1})$/

// Card validation helpers
export const validateCardNumberWithLuhn = (cardNumber: string): boolean => {
  const digits = cardNumber.replace(/\s/g, '')
  if (!/^\d+$/.test(digits) || digits.length < 13 || digits.length > 19) return false

  // Luhn algorithm
  let sum = 0
  let isEven = false
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = Number.parseInt(digits[i], 10)
    if (isEven) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
    isEven = !isEven
  }
  return sum % 10 === 0
}

export const validateCardExpiry = (expiry: string): boolean => {
  if (!/^\d{2}\/\d{2}$/.test(expiry)) return false

  const [monthStr, yearStr] = expiry.split('/')
  const month = Number.parseInt(monthStr, 10)
  const year = Number.parseInt(yearStr, 10)

  if (month < 1 || month > 12) return false

  const now = new Date()
  const currentYear = now.getFullYear() % 100
  const currentMonth = now.getMonth() + 1

  // Card is expired if year is in past, or same year but month is in past
  if (year < currentYear || (year === currentYear && month < currentMonth)) return false

  return true
}

/**
 * Card number validation rules for bulleted display
 */
export const cardNumberValidationRules: ValidationRule[] = [
  { test: (v) => v.length > 0, message: 'Card number is required' },
  {
    test: (v) => {
      const digits = v.replace(/\s/g, '')
      return digits.length >= 13 && digits.length <= 19
    },
    message: 'Must be between 13 and 19 digits',
  },
  { test: (v) => validateCardNumberWithLuhn(v), message: 'Invalid card number' },
]

/**
 * Card expiry validation rules for bulleted display
 */
export const cardExpiryValidationRules: ValidationRule[] = [
  { test: (v) => v.length > 0, message: 'Expiry date is required' },
  { test: (v) => /^\d{2}\/\d{2}$/.test(v), message: 'Must be in MM/YY format' },
  {
    test: (v) => {
      if (!/^\d{2}\/\d{2}$/.test(v)) return true // Skip if format invalid
      const month = Number.parseInt(v.split('/')[0], 10)
      return month >= 1 && month <= 12
    },
    message: 'Invalid month (must be 01-12)',
  },
  {
    test: (v) => {
      if (!/^\d{2}\/\d{2}$/.test(v)) return true // Skip if format invalid
      return validateCardExpiry(v)
    },
    message: 'Card has expired',
  },
]

/**
 * CVC/CVV validation rules for bulleted display
 */
export const cardCvcValidationRules: ValidationRule[] = [
  { test: (v) => v.length > 0, message: 'CVV is required' },
  { test: (v) => v.length >= 3, message: 'Must be at least 3 digits' },
  { test: (v) => v.length <= 4, message: 'Must be at most 4 digits' },
  { test: (v) => /^\d+$/.test(v), message: 'Must contain only digits' },
]

/**
 * Card holder name validation rules for bulleted display
 */
export const cardHolderNameValidationRules: ValidationRule[] = [
  { test: (v) => v.length > 0, message: 'Card holder name is required' },
  { test: (v) => v.length <= 100, message: 'Cannot exceed 100 characters' },
  {
    test: (v) => /^[\p{L}\p{M}\s'.-]+$/u.test(v),
    message: 'Can only contain letters, spaces, hyphens, apostrophes, and periods',
  },
]

/**
 * Multi-rule card number schema with bulleted errors
 */
export const cardNumberMultiRuleSchema = () => createMultiRuleValidation(cardNumberValidationRules)

/**
 * Multi-rule card expiry schema with bulleted errors
 */
export const cardExpiryMultiRuleSchema = () => createMultiRuleValidation(cardExpiryValidationRules)

/**
 * Multi-rule CVC/CVV schema with bulleted errors
 */
export const cardCvcMultiRuleSchema = () => createMultiRuleValidation(cardCvcValidationRules)

/**
 * Multi-rule card holder name schema with bulleted errors
 */
export const cardHolderNameMultiRuleSchema = () =>
  createMultiRuleValidation(cardHolderNameValidationRules)

export const validateGstWithRegex = (gstNumber: string): boolean => GST_REGEX.test(gstNumber)

// Schema for immediate validation during typing
export const gstValidationSchema = z
  .string()
  .min(1, 'GST number is required')
  .refine((value) => value.length === 0 || validateGstWithRegex(value), 'Invalid GST number format')

// ZIP Code validation regex patterns
export const US_ZIP_REGEX = /(^\d{5}$)|(^\d{5}-\d{4}$)/
export const CA_ZIP_REGEX = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][\s-]?\d[ABCEGHJ-NPRSTV-Z]\d$/i

export const validateZipCodeWithRegex = (zipCode: string, country: string): boolean => {
  switch (country) {
    case 'US':
      return US_ZIP_REGEX.test(zipCode)
    case 'CA':
      return CA_ZIP_REGEX.test(zipCode)
    case 'IN':
      return zipCode.length === 6
    default:
      // For other countries, ZIP must be less than 8 characters
      return zipCode.length > 0 && zipCode.length < 8
  }
}

// Schema for immediate validation during typing
export const zipCodeValidationSchema = (country: string) => {
  const fieldName = getPostalCodeLabel(country)
  return z
    .string()
    .min(1, `${fieldName} is required`)
    .refine(
      (value) => value.length === 0 || validateZipCodeWithRegex(value, country),
      () => ({ message: `Invalid ${fieldName.toLowerCase()}` }),
    )
}

export const getGstEndIcon = (status: ValidationStatusType) => {
  switch (status) {
    case ValidationStatus.VALID:
      return (
        <Tooltip content="GST number is valid and active">
          <CheckCircle2 className="h-4 w-4 text-status-success" />
        </Tooltip>
      )
    case ValidationStatus.INVALID:
      return (
        <Tooltip content="Invalid or inactive GST number">
          <AlertOctagon className="h-4 w-4 text-destructive" />
        </Tooltip>
      )
    case ValidationStatus.LOADING:
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
    default:
      return null
  }
}

export const getZipCodeEndIcon = (status: ValidationStatusType) => {
  switch (status) {
    case ValidationStatus.VALID:
      return (
        <Tooltip content="ZIP Code is valid">
          <CheckCircle2 className="h-4 w-4 text-status-success" />
        </Tooltip>
      )
    case ValidationStatus.INVALID:
      return (
        <Tooltip content="Invalid ZIP Code">
          <AlertOctagon className="h-4 w-4 text-destructive" />
        </Tooltip>
      )
    case ValidationStatus.LOADING:
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
    default:
      return null
  }
}

// Plan update polling config (used after upgrade to wait for profile refresh)
export const PLAN_UPDATE_MAX_POLL_ATTEMPTS = 5
export const PLAN_UPDATE_POLL_INTERVAL_MS = 3000
